/* eslint-disable react/destructuring-assignment */
import { FormEvent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// material-ui
import FirstPageIcon from '@mui/icons-material/FirstPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import LastPageIcon from '@mui/icons-material/LastPage';
import {
    Box,
    Button,
    Card,
    CardContent,
    Collapse,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Typography,
    useTheme
} from '@mui/material';

// import { DemoContainer, DemoItem } from '@mui/x-date-pickers/internals/demo';

// project imports
import { dispatch, useSelector } from 'store';
import MainCard from 'ui-component/cards/MainCard';
import { gridSpacing } from 'store/constant';

import dayjs, { Dayjs } from 'dayjs';

// Icons
import { useIntl } from 'react-intl';
import { getLogsService } from 'store/slices/logger';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import useDateAdapter from 'hooks/useDateAdapter';
import ReactJson from 'react-json-view';
import { Close } from '@mui/icons-material';
import { DateTimeValidationError } from '@mui/x-date-pickers/internals/hooks/validation/useDateTimeValidation';
import IntegrationEventsAreaChartCard from 'views/dashboard/Analytics/IntegrationEventsAreaChartCard';

interface TablePaginationActionsProps {
    count: number;
    page: number;
    rowsPerPage: number;
    onPageChange: (event: React.MouseEvent<HTMLButtonElement>, newPage: number) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, 0);
    };

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    const handleLastPageButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
    };

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5 }}>
            <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                {theme.direction === 'rtl' ? <LastPageIcon /> : <FirstPageIcon />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
                {theme.direction === 'rtl' ? <FirstPageIcon /> : <LastPageIcon />}
            </IconButton>
        </Box>
    );
}

type FilterParams = { endTime: Dayjs; startTime: Dayjs; term?: string };

const LogsHistory = () => {
    // hooks
    const theme = useTheme();

    // store
    const { logs, isLoading } = useSelector((state) => state.logger);

    // state
    const [showLog, setShowLog] = useState<any[] | null>(null);
    const [series, setSeries] = useState<{ name: string; data: any }[] | undefined>();
    const [totals, setTotals] = useState<
        | {
              pSuccess: string;
              pError: string;
              sSuccess: string;
              sError: string;
              events: string;
              range: string;
              sInventorySuccess: string;
              sPricesSuccess: string;
          }
        | undefined
    >();

    // results
    const [searchParams, setSearchParams] = useState<{ startTimeValue: Dayjs; endTimeValue: Dayjs; textParam: string } | undefined>(
        undefined
    );

    const { logType } = useParams();

    const getHistory = ({ endTime, startTime, term }: FilterParams) => {
        setShowLog(null);

        dispatch(
            getLogsService({
                endTime: endTime.toISOString(),
                startTime: startTime.toISOString(),
                log: logType!,
                term
            })
        );

        setSearchParams({
            startTimeValue: startTime,
            endTimeValue: endTime,
            textParam: term ?? ''
        });
    };

    useEffect(() => {
        const currentDate = dayjs();
        const initialDate = dayjs().add(-1, 'h');

        getHistory({
            endTime: currentDate,
            startTime: initialDate
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onRowClick = (log: any[]) => {
        setShowLog(log);
    };

    const fetchData = async () => {
        const roundedToTenMinutes = (date: Date) => {
            const minutes = Math.floor(date.getMinutes() / 10) * 10;
            date.setMinutes(minutes, 0, 0);
            return date;
        };

        const logEvents = logs
            .map((log) => {
                if (log.length > 0 && log[0] && log[1]) {
                    const date = new Date(log[0]);
                    return {
                        id: log[1],
                        time: roundedToTenMinutes(date).getTime()
                    };
                }
                return null;
            })
            .filter((log): log is { id: string; time: number } => log !== null);

        const initialAccumulator: { [key: string]: number } = {};

        const logsByTenMinutes = logEvents.reduce((acc, log) => {
            const timeKey = log.time.toString();
            if (!acc[timeKey]) {
                acc[timeKey] = 0;
            }
            acc[timeKey] += 1;
            return acc;
        }, initialAccumulator);

        const seriesData = Object.keys(logsByTenMinutes).map((time) => ({
            x: new Date(parseInt(time, 10)).toISOString(),
            y: logsByTenMinutes[time]
        }));

        setSeries([{ name: 'Logs per Ten Minutes', data: seriesData }]);

        const totalEvents = logEvents.length;
        let range = 'No logs available';

        if (logEvents.length > 0) {
            range = `from ${new Date(logEvents[0].time).toLocaleString()} to ${new Date(
                logEvents[logEvents.length - 1].time
            ).toLocaleString()}`;
        }

        setTotals({
            pSuccess: '0',
            pError: '0',
            sSuccess: '0',
            sError: '0',
            sInventorySuccess: '0',
            sPricesSuccess: '0',
            events: totalEvents.toString(),
            range
        });
    };

    useEffect(() => {
        if (isLoading) return;
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading]);

    return (
        <MainCard
            title={
                <Grid container>
                    <Grid item sm={6}>
                        <Typography variant="h2" sx={{ fontWeight: 700 }}>
                            {logType}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Events history
                        </Typography>
                    </Grid>
                </Grid>
            }
        >
            <CardContent sx={{ p: 0 }}>
                <Grid container spacing={gridSpacing}>
                    <Grid item xs={12}>
                        <ParamsSearcher handleSearch={getHistory} />
                    </Grid>
                </Grid>
                {isLoading ? (
                    <Box sx={{ mt: 4 }}>
                        <LinearProgress />
                    </Box>
                ) : (
                    <Stack sx={{ mt: 4 }}>
                        <Collapse in={Boolean(searchParams)}>
                            {series && series[0].data.length && totals?.range && (
                                <IntegrationEventsAreaChartCard series={series} totals={totals} isLogs />
                            )}
                        </Collapse>
                        <Stack direction="row" justifyContent="space-between">
                            <Stack sx={{ width: showLog ? 0.58 : 1, transition: 'all 420ms linear', p: 1 }}>
                                <RenderEvents logs={logs} onRowClick={onRowClick} />
                            </Stack>
                            <Stack sx={{ width: showLog ? 0.4 : 0, overflow: 'hidden', p: showLog ? 1 : 0 }}>
                                <Card elevation={2} sx={{ p: 2, background: theme.palette.background.paper }}>
                                    <Stack>
                                        <Stack direction="row" justifyContent="flex-end">
                                            <IconButton onClick={() => setShowLog(null)}>
                                                <Close />
                                            </IconButton>
                                        </Stack>
                                        <Divider sx={{ mt: 1, mb: 1, display: 'block' }} />
                                        <ReactJson
                                            style={{ marginTop: 10 }}
                                            src={showLog ?? {}}
                                            theme="twilight"
                                            enableClipboard
                                            displayDataTypes={false}
                                        />
                                    </Stack>
                                </Card>
                            </Stack>
                        </Stack>{' '}
                    </Stack>
                )}
            </CardContent>
        </MainCard>
    );
};

export default LogsHistory;

const ParamsSearcher = ({ handleSearch }: { handleSearch: (data: FilterParams) => void }) => {
    // hooks
    const intl = useIntl();

    // store
    const { isLoading } = useSelector((state) => state.logger);

    // STATE
    const [hasError, setHasError] = useState<boolean>(false);

    // before search
    const [endTimeValue, setEndTimeValue] = useState<Dayjs | null>(dayjs());
    const [startTimeValue, setStartTimeValue] = useState<Dayjs | null>(dayjs().add(-1, 'h'));
    const [textParam, setTextParam] = useState<string>('');
    const [startError, setStartError] = useState<boolean>(false);
    const [endError, setEndError] = useState<boolean>(false);

    const checkErrors = () => startError || endError || isLoading;

    const handleSubmitSearch = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (startTimeValue && endTimeValue && startTimeValue.isBefore(endTimeValue)) {
            handleSearch({
                endTime: endTimeValue,
                startTime: startTimeValue,
                term: textParam
            });
        } else {
            setHasError(true);
            console.log(hasError);
        }
    };

    return (
        <Stack component="form" onSubmit={handleSubmitSearch} direction="row" alignItems="center" justifyContent="flex-end">
            <Box sx={{ mr: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        minDateTime={endTimeValue?.subtract(72, 'hours')}
                        maxDateTime={endTimeValue ? endTimeValue.subtract(1, 'hour') : dayjs()}
                        renderInput={(props) => <TextField {...props} required />}
                        onChange={(newDate) => {
                            setStartTimeValue(newDate);
                        }}
                        onError={(reason: DateTimeValidationError) => {
                            setStartError(Boolean(reason));
                        }}
                        value={startTimeValue}
                        label="Start date"
                    />
                </LocalizationProvider>
            </Box>

            <Box sx={{ mr: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                        maxDateTime={dayjs()}
                        renderInput={(props) => <TextField {...props} required />}
                        onChange={(newDate) => {
                            setEndTimeValue(newDate);
                        }}
                        onError={(reason: DateTimeValidationError) => {
                            setEndError(Boolean(reason));
                        }}
                        value={endTimeValue}
                        label="End date"
                    />
                </LocalizationProvider>
            </Box>

            <TextField
                value={textParam}
                onChange={(e) => {
                    setTextParam(e.target.value);
                }}
                type="string"
                label="Search term"
                sx={{ mr: 2, width: 200 }}
            />

            <Button size="small" sx={{ ml: 1 }} variant="contained" type="submit" disabled={checkErrors()}>
                {intl.formatMessage({ id: 'search' })}
            </Button>
        </Stack>
    );
};

const RenderEvents = ({ logs, onRowClick }: { logs: any[]; onRowClick: (log: any[]) => void }) => {
    const { dateFormat } = useDateAdapter();

    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [page, setPage] = useState(0);

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
        setPage(newPage);
    };

    if (logs.length === 0) {
        return (
            <Stack sx={{ pt: 6 }} alignItems="center" justifyContent="center">
                <Typography variant="caption" color="error" sx={{ opacity: 0.6 }}>
                    There are no events with this filters.
                </Typography>
            </Stack>
        );
    }

    return (
        <Card elevation={2}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell>Event</TableCell>
                            {/* <TableCell>Type</TableCell> */}
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(rowsPerPage > 0 ? logs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : logs).map((row, index) => (
                            <TableRow
                                key={`rowDetail-${index}`}
                                sx={{ cursor: 'pointer', '&:last-child td, &:last-child th': { border: 0 } }}
                                onClick={() => onRowClick(row)}
                            >
                                <TableCell component="th" scope="row">
                                    {dateFormat(row[0])}
                                </TableCell>
                                <TableCell component="th" scope="row">
                                    {row[1]}
                                </TableCell>
                                {/* <TableCell component="th" scope="row">
                                    {row[2]}
                                </TableCell> */}
                                <TableCell component="th" scope="row">
                                    <Typography variant="caption">{JSON.stringify([...row]?.reverse()).slice(0, 100)}</Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                    <TableFooter>
                        <TableRow>
                            <TableCell colSpan={7}>
                                <TablePagination
                                    component="div"
                                    rowsPerPageOptions={[100, 150, 200, { label: 'All', value: -1 }]}
                                    count={logs.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                />
                            </TableCell>
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Card>
    );
};
